import type React from 'react';

import { Text } from '@change/ds-core-ui';

import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { TikTokPlayer } from './components/TikTokPlayer';
import { useVideoFirstHero } from './hooks/useVideoFirstHero';
import type { VideoFirstViewport } from './hooks/useVideoFirstHero';

const VideoFirstHeroClient: React.FC<Props> = ({
	fallback,
	showTitleOnDesktop = true,
	sourceUrl,
	viewport,
}): React.ReactNode => {
	const {
		data: { playerUrl, shouldRender, title, titleDataQa },
	} = useVideoFirstHero(sourceUrl, viewport);

	if (!shouldRender || !playerUrl) return fallback;

	return (
		<div
			className="overflow-hidden bg-black text-white md:flex md:flex-col md:items-center md:rounded-xl"
			data-qa="video-first-hero"
		>
			<div className="relative mx-auto h-[70svh] w-full max-w-[480px] bg-black md:aspect-[9/16] md:h-auto md:max-w-[420px]">
				<TikTokPlayer playerUrl={playerUrl} title={title} />
			</div>
			<div
				className={showTitleOnDesktop ? 'w-full max-w-[760px] px-4 py-4 md:p-8 md:text-center' : 'px-4 py-4 md:hidden'}
			>
				<Text
					as="h1"
					variant="heading"
					fontWeight="bold"
					data-qa={titleDataQa}
					className="text-2xl leading-tight text-wrap md:text-center md:text-4xl"
				>
					{title}
				</Text>
			</div>
		</div>
	);
};

export const VideoFirstHero: React.FC<Props> = ({ fallback, showTitleOnDesktop, sourceUrl, viewport }) => {
	return (
		<ClientRender placeholder={fallback}>
			{() => (
				<VideoFirstHeroClient
					fallback={fallback}
					showTitleOnDesktop={showTitleOnDesktop}
					sourceUrl={sourceUrl}
					viewport={viewport}
				/>
			)}
		</ClientRender>
	);
};

type Props = {
	fallback: React.ReactNode;
	showTitleOnDesktop?: boolean;
	sourceUrl: string;
	viewport: VideoFirstViewport;
};
